import type { AuthStatus, LoginRequest, MeResponse, SetupRequest } from '@shared/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api';
import { queryKeys } from './query';

/** First-run/auth state. Cheap, always fresh — it decides which screen the user sees. */
export function useAuthStatus() {
  return useQuery({
    queryKey: queryKeys.authStatus,
    queryFn: () => api.get<AuthStatus>('/api/auth/status'),
    staleTime: 0,
  });
}

export function useMe(enabled = true) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.get<MeResponse>('/api/me'),
    enabled,
    retry: false,
  });
}

export function useLogin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginRequest) => api.post<{ ok: true }>('/api/auth/login', input),
    onSuccess: async () => {
      await client.invalidateQueries();
    },
  });
}

export function useSetup() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SetupRequest) => api.post<{ ok: true }>('/api/auth/setup', input),
    onSuccess: async () => {
      await client.invalidateQueries();
    },
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/api/auth/logout'),
    onSuccess: () => {
      client.clear();
    },
  });
}

export function useSwitchWorkspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) => api.post<{ ok: true }>('/api/me/workspace', { workspaceId }),
    onSuccess: async () => {
      await client.invalidateQueries();
    },
  });
}

/** Field-level errors from the API, ready to hand to form fields. */
export function fieldErrors(error: unknown): Record<string, string> {
  return error instanceof ApiError ? (error.fields ?? {}) : {};
}

export function errorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  // ApiError extends Error, so its server-supplied message is covered here too.
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Something went wrong. Please try again.';
}
